import React, { useCallback } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Dispatch } from 'redux'
import { AcatsSearchDto } from '../../api/types'
import { RootState } from '../../store'
import { SearchContext } from '../contexts/search'
import { SearchProps } from '../contexts/search/SearchContext'
import {
  getAccountDetails,
  getAccountOwners,
  getCorrespondent,
  searchAccounts,
  searchTransfers,
} from '../../actions'
import { History } from 'history'
import { useToastErrorHandler } from '../../hooks'

interface SearchProviderComponentProps extends PropsFromRedux {
  children: JSX.Element | JSX.Element[]
}

const SearchProviderComponent = ({
  accountDetailsState,
  accountOwnerState,
  accountSearchState,
  correspondentState,
  children,
  getAccountDetails,
  getAccountOwners,
  getCorrespondent,
  searchAccounts,
  searchTransfers,
  transferSearchState,
}: SearchProviderComponentProps) => {
  const onError = useToastErrorHandler()
  const searchStateAndActions: SearchProps = {
    accountDetailsState,
    accountOwnerState,
    accountSearchState,
    correspondentState,
    getAccountDetails: useCallback(
      (accountNumber: string) => getAccountDetails(accountNumber, onError),
      [getAccountDetails, onError]
    ),
    getAccountOwners: useCallback(
      (accountNumber: string) => getAccountOwners(accountNumber, onError),
      [getAccountOwners, onError]
    ),
    getCorrespondent: useCallback(
      (accountNumber: string) => getCorrespondent(accountNumber, onError),
      [getCorrespondent, onError]
    ),
    searchAccounts,
    searchTransfers,
    transferSearchState,
  }
  return (
    <SearchContext.Provider value={searchStateAndActions}>
      {children}
    </SearchContext.Provider>
  )
}

const mapStateToProps = ({
  accountDetailsReducer: accountDetailsState,
  accountOwnersReducer: accountOwnerState,
  accountsReducer: accountSearchState,
  correspondentReducer: correspondentState,
  transfersReducer: transferSearchState,
}: RootState) => ({
  accountDetailsState,
  accountOwnerState,
  accountSearchState,
  correspondentState,
  transferSearchState,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getAccountDetails: (
    accountNumber: string,
    onError: (error: string) => void
  ) => getAccountDetails(dispatch, accountNumber, onError),
  getAccountOwners: (accountNumber: string, onError: (error: string) => void) =>
    getAccountOwners(dispatch, accountNumber, onError),
  getCorrespondent: (accountNumber: string, onError: (error: string) => void) =>
    getCorrespondent(dispatch, accountNumber, onError),
  searchAccounts: (searchTerm: string, history: History) =>
    searchAccounts(dispatch, searchTerm, history),
  searchTransfers: (history: History, acatsSearchDto?: AcatsSearchDto) =>
    searchTransfers(dispatch, history, acatsSearchDto),
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export const SearchProvider = connector(SearchProviderComponent)
